import { DateTime } from 'luxon';
import { format, fromUnixTime } from 'date-fns';

import {
  ActionFlow,
  PaymentPeriod,
  IPlan,
  PlanType,
  PRICING_PLAN,
  REQUIREMENTS,
} from '../../../components/subscription-change-modal/constant';

export const calculateAnnualPrice = (
  integer: number,
  decimal: number
): string => {
  const fractionPart = (decimal * 12) / 100;
  let wholeNumber = (integer * 12 + fractionPart).toString();
  if (Number(wholeNumber) > 999) {
    wholeNumber = `${wholeNumber.slice(0, 1)},${wholeNumber.slice(1)}`;
  }
  return wholeNumber;
};

export const getSubscriptionName = (
  status: string,
  subscriptionName: string
): { plan: PlanType | ''; paymentPeriod: PaymentPeriod | '' } => {
  if (subscriptionName === 'team-monthly')
    return { plan: 'Team', paymentPeriod: 'month' };
  if (subscriptionName === 'team-annual')
    return { plan: 'Team', paymentPeriod: 'year' };
  if (subscriptionName === 'professional-annual')
    return { plan: 'Professional', paymentPeriod: 'year' };
  if (subscriptionName === 'professional-monthly')
    return { plan: 'Professional', paymentPeriod: 'month' };
  if (subscriptionName === 'starter-monthly')
    return { plan: 'Starter', paymentPeriod: 'month' };
  if (subscriptionName === 'starter-annual')
    return { plan: 'Starter', paymentPeriod: 'year' };
  if (subscriptionName === 'free')
    return { plan: 'Free', paymentPeriod: 'month' };
  return { plan: '', paymentPeriod: '' };
};

export const getPlan = (subscriptionName: string): IPlan => {
  const extractPricingPlan = PRICING_PLAN.filter(plan => {
    if (
      subscriptionName?.toLowerCase().search(plan?.plan?.toLowerCase()) !== -1
    ) {
      return plan;
    }
    return false;
  });
  return extractPricingPlan[0];
};

export const getCalculationFlow = (
  defaultPlan: IPlan | undefined,
  newPlan: IPlan
): ActionFlow => {
  if (defaultPlan === undefined) return 'downgrade';

  if (defaultPlan.priority > newPlan.priority) {
    return 'downgrade';
  }
  if (defaultPlan.priority < newPlan.priority) {
    return 'upgrade';
  }
  return 'downgrade';
};

export const isMinumumRequirementsOK = (
  defaultPlan: REQUIREMENTS,
  newPlan: REQUIREMENTS
): boolean => {
  const {
    datasources: defaultDatasources,
    workspace: defaultWorkspaces,
    user: defaultUsers,
  } = defaultPlan;

  if (newPlan === undefined) return false;

  if (defaultDatasources > newPlan.datasources) return false;
  if (defaultWorkspaces > newPlan.workspace) return false;
  if (defaultUsers > newPlan.user) return false;
  return true;
};

export const calculateCouponAmount = (
  type: string,
  amount: number,
  plan: IPlan,
  isAnnualPaymentSelected: boolean
): number => {
  if (type === 'fixed_amount') return amount;

  if (isAnnualPaymentSelected)
    return (
      (amount *
        Number(
          `${plan.annualPrice.integer * 12}.${plan.annualPrice.decimal}`
        )) /
      100
    );

  return (
    (amount *
      (plan.monthlyPrice.integer + Number(`.${plan.monthlyPrice.decimal}`))) /
    100
  );
};

export const getSubscription = (subscriptionName: string): string => {
  if (subscriptionName.toLowerCase().includes('team')) {
    return 'Team';
  }
  if (subscriptionName.toLowerCase().includes('pro')) {
    return 'Pro';
  }
  if (subscriptionName.toLowerCase().includes('starter')) {
    return 'Starter';
  }

  if (subscriptionName.toLowerCase().includes('free')) {
    return 'Free';
  }

  return '';
};

export const dateOrdinal = (date: number) => {
  if (date === 31 || date === 21 || date === 1) return `${date}st`;
  if (date === 22 || date === 2) return `${date}nd`;
  if (date === 23 || date === 3) return `${date}rd`;
  return `${date}th`;
};

export const get14days = () => {
  const currentDate = new Date();
  const d = currentDate.setDate(currentDate.getDate() + 14);

  function prettyDate(date: Date) {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    return `${months[date.getUTCMonth()]} ${dateOrdinal(date.getUTCDate())}`;
  }

  return prettyDate(new Date(d));
};

export const formatAmount = (value: number): string => {
  if (value === 0) return '0';
  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  return formatter.format(value);
};

export const getFormattedDate = (
  date: number | string,
  dateFormat: string
): string => {
  if (typeof date === 'string') {
    return format(new Date(date), dateFormat);
  }
  if (date.toString().length === 10) {
    return format(fromUnixTime(date), dateFormat);
  }
  return format(new Date(date), dateFormat);
};
